/**
 * What the shop is on, how much of it is used, and how to get more.
 *
 * The order is deliberate and is not the order the data arrives in. A person
 * opens this screen for one of three reasons — "what am I paying?", "why can I
 * not add another till?", "how do I get a bigger package?" — so the package,
 * the meters and the upgrade sit in that order down the page, and anything owed
 * sits at the top where a shop that is about to be suspended will see it.
 *
 * Upgrading is a **request**, not a change. SmartSpaza moves a merchant between
 * packages (docs/TENANT-MANAGEMENT.md §10.1); this screen records what the shop
 * wants, acknowledges it and shows it back to them until somebody deals with it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorState,
  Pill,
  SkeletonRows,
  UsageMeter,
} from "@/components/ui";
import { formatMoney } from "@/lib/format";
import { billingRepository, type PackageOffer } from "@/repositories/billingRepository";
import { useAuthStore } from "@/stores/authStore";

const STATUS_TONE = {
  TRIAL: "info",
  ACTIVE: "success",
  PAST_DUE: "warn",
  SUSPENDED: "danger",
  CANCELLED: "neutral",
  EXPIRED: "neutral",
} as const;

const STATUS_LABEL = {
  TRIAL: "On trial",
  ACTIVE: "Active",
  PAST_DUE: "Past due",
  SUSPENDED: "Suspended",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
} as const;

export function MySubscription() {
  const client = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const mayAsk = can("billing.request_package_change");

  const [choosing, setChoosing] = useState<PackageOffer | null>(null);
  const [error, setError] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ["billing", "overview"],
    queryFn: () => billingRepository.overview(),
  });

  const packages = useQuery({
    queryKey: ["billing", "packages"],
    queryFn: () => billingRepository.packages(),
  });

  const ask = useMutation({
    mutationFn: ({ code, note }: { code: string; note: string }) =>
      billingRepository.requestPackage(code, note),
    onSuccess: () => {
      setError(null);
      setChoosing(null);
      void client.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? caught.detail : "That request could not be sent.",
      ),
  });

  const withdraw = useMutation({
    mutationFn: () => billingRepository.withdrawRequest(),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["billing"] }),
  });

  if (overview.isError) {
    return <ErrorState title="Your account could not load" onRetry={() => overview.refetch()} />;
  }

  if (overview.isLoading || !overview.data) {
    return <SkeletonRows rows={6} />;
  }

  const { subscription, usage, outstanding, open_invoice_count, pending_request } = overview.data;
  const owes = Number(outstanding) > 0;

  return (
    <div className="space-y-5">
      {error && (
        <p className="rounded-card bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</p>
      )}

      {owes && (
        <Card className="border-warn-500/40 bg-warn-50/60">
          <p className="text-sm text-ink-900">
            <span className="font-medium">{formatMoney(outstanding)} is outstanding</span> across{" "}
            {open_invoice_count} {open_invoice_count === 1 ? "invoice" : "invoices"}. Settle it to
            keep your shop trading.
          </p>
        </Card>
      )}

      {/* ── What you are on ───────────────────────────────────────────────── */}
      {subscription ? (
        <Card>
          <CardHeader
            title={subscription.package}
            subtitle={subscription.package_description || "Your SmartSpaza package"}
            action={
              <Pill tone={STATUS_TONE[subscription.status]}>
                {STATUS_LABEL[subscription.status]}
              </Pill>
            }
          />

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Fact
              label={subscription.billing_cycle === "ANNUAL" ? "Per year" : "Per month"}
              value={formatMoney(subscription.price)}
            />
            <Fact label="This period ends" value={subscription.current_period_end} />
            {subscription.trial_end_date && (
              <Fact label="Trial ends" value={subscription.trial_end_date} />
            )}
            {subscription.cancel_at_period_end && (
              <Fact label="Ends" value="At the end of this period" />
            )}
          </dl>
        </Card>
      ) : (
        <Card>
          <EmptyState
            title="No package yet"
            description="SmartSpaza is still setting your account up. You will be told when it is ready."
          />
        </Card>
      )}

      {/* ── What you have used ────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title="What you are using"
          subtitle="Counted now, not cached — archive something and it moves"
        />
        <div className="mt-4 space-y-4">
          {usage.map((line) => (
            <UsageMeter
              key={line.key}
              label={line.label}
              used={line.used}
              limit={line.limit}
              percent={line.percent}
              isExhausted={line.is_exhausted}
            />
          ))}
        </div>
      </Card>

      {/* ── Asking for something bigger ───────────────────────────────────── */}
      {pending_request ? (
        <Card>
          <CardHeader
            title={`You asked for ${pending_request.requested_package}`}
            subtitle={`Requested on ${pending_request.created_at.slice(0, 10)}. SmartSpaza will be in touch.`}
            action={
              mayAsk ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={withdraw.isPending}
                  onClick={() => withdraw.mutate()}
                >
                  Withdraw
                </Button>
              ) : undefined
            }
          />
          {pending_request.note && (
            <p className="mt-3 rounded-card bg-canvas px-3 py-2 text-sm text-ink-600">
              “{pending_request.note}”
            </p>
          )}
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="Other packages"
            subtitle={
              mayAsk
                ? "Ask SmartSpaza to move you — nothing changes until they do"
                : "Ask the owner to request a change"
            }
          />

          {packages.isError ? (
            <ErrorState title="Packages could not load" onRetry={() => packages.refetch()} />
          ) : packages.isLoading ? (
            <SkeletonRows rows={3} />
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {packages.data?.map((offer) => (
                <PackageCard
                  key={offer.id}
                  offer={offer}
                  canAsk={mayAsk}
                  onAsk={() => setChoosing(offer)}
                />
              ))}
            </div>
          )}
        </Card>
      )}

      {choosing && (
        <AskDialog
          offer={choosing}
          pending={ask.isPending}
          onClose={() => setChoosing(null)}
          onConfirm={(note) => ask.mutate({ code: choosing.code, note })}
        />
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium tabular-nums text-ink-900">{value}</dd>
    </div>
  );
}

function PackageCard({
  offer,
  canAsk,
  onAsk,
}: {
  offer: PackageOffer;
  canAsk: boolean;
  onAsk: () => void;
}) {
  return (
    <div
      className={
        offer.is_current
          ? "rounded-card border-2 border-brand-500 bg-brand-50/40 p-4"
          : "rounded-card border border-line p-4"
      }
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink-900">{offer.name}</h3>
        {offer.is_current && <Pill tone="success">Current</Pill>}
      </div>

      <p className="mt-1 text-lg font-semibold tabular-nums text-ink-900">
        {formatMoney(offer.monthly_price)}
        <span className="ml-1 text-xs font-normal text-ink-500">a month</span>
      </p>

      {offer.description && <p className="mt-2 text-sm text-ink-600">{offer.description}</p>}

      {!offer.is_current && canAsk && (
        <Button variant="secondary" size="sm" className="mt-3 w-full" onClick={onAsk}>
          Ask for {offer.name}
        </Button>
      )}
    </div>
  );
}

function AskDialog({
  offer,
  pending,
  onClose,
  onConfirm,
}: {
  offer: PackageOffer;
  pending: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState("");

  return (
    <Dialog open title={`Ask for ${offer.name}`} onClose={onClose}>
      <p className="text-sm text-ink-600">
        This sends SmartSpaza a request. Nothing about your account changes until somebody there
        arranges it, and they will be in touch first.
      </p>

      <label className="mt-4 block">
        <span className="text-sm font-medium text-ink-900">
          Anything they should know? <span className="font-normal text-ink-500">(optional)</span>
        </span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          autoFocus
          placeholder="We are opening a second shop in November"
          className="mt-2 w-full rounded-control border border-line px-3 py-2 text-sm"
        />
      </label>

      <div className="mt-4 flex gap-2">
        <Button variant="primary" disabled={pending} onClick={() => onConfirm(note)}>
          Send the request
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}
